import React from "react";
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import PageAbstract from "components/PageAbstract";
import { AICalendarProvider } from "contexts/AICalendarContext";
import ContentCalendarWizard from "./components/AICalendarWizard/ContentCalendarWizard";
import i18n from "i18";

import _request from "../../actions/_request";
import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import { enUS, ru, fr, de, it } from "date-fns/locale";
import IconsAccounts from "../../components/IconsAccounts";
import ImageError from "../../components/ImageError";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import NotesIcon from "@mui/icons-material/Notes";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import {
  ALL_GROUPS,
  buildScheduledEdit,
  filterTasksByGroup,
  getDayEntries,
  getPostInfo,
  getPostStatus,
} from "./postData";
import PublishStatusDialog from "../post/components/PublishStatusDialog";
// import { useNavigate } from "react-router-dom";

import "./style.css";
import moment from "moment-timezone";
import "moment/locale/ru";
import "moment/locale/fr";
import "moment/locale/de";
import "moment/locale/it";

// Ensure moment uses the correct locale
moment.locale("en"); // Set default locale

// What fits in a day cell of a laptop-sized calendar; the rest is counted ("+N") and shown in the posts panel.
const MAX_CHIPS_PER_DAY = 2;

const STATUS_COLORS = {
  scheduled: "#f0c14b",
  published: "#5ed3a0",
  failed: "#f08a8a",
};

const chipSx = (color) => ({
  padding: "0 8px",
  borderRadius: "10px",
  fontSize: 11,
  fontWeight: "bold",
  color,
  border: `1px solid ${color}`,
});

export default function SchedulePage(props) {
  // const navigate = useNavigate();

  return (
    <AICalendarProvider>
      <Schedule {...props} />
    </AICalendarProvider>
  );
  // return <Schedule {...props} navigate={navigate} />;
}

class Schedule extends PageAbstract {
  constructor(props) {
    super(props);

    this.state = {
      ...this.state,
      currentMonth: new Date(),
      selectedDate: new Date(),
      accounts: [],
      // Account groups and the one the calendar is limited to (OP#3477). The filter is shown only when there are groups.
      groups: [],
      selectedGroupId: ALL_GROUPS,
      postTasks: [],
      users: [],
      currentDay: new Date(),
      selectedSocialType: "All",
      open: false,
      anchorEl: null,
      openMenuIndex: false,
      preparedPostsCache: null,
      lastPostTasksLength: 0,
      wizardOpen: false,
      // Post whose publication status dialog is open (a Threads chain: per-part status, "Continue the thread").
      statusPostId: null,
    };

    this.toggleDrawer = this.toggleDrawer.bind(this);
    this.getWeekdays = this.getWeekdays.bind(this);

    // Debounce для подготовки данных
    this.debouncedPreparePanelData = this.debounce(
      this.preparePanelData.bind(this),
      50
    );
  }

  // Простая функция debounce
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  componentDidMount() {
    // Set the correct locale when component mounts
    const momentLocale = this.getMomentLocale();
    moment.locale(momentLocale);

    // Listen for language changes
    i18n.on("languageChanged", this.handleLanguageChange);

    // Call parent componentDidMount if it exists
    if (super.componentDidMount) {
      super.componentDidMount();
    }
  }

  componentWillUnmount() {
    // Remove language change listener
    i18n.off("languageChanged", this.handleLanguageChange);

    // Call parent componentWillUnmount if it exists
    if (super.componentWillUnmount) {
      super.componentWillUnmount();
    }
  }

  handleLanguageChange = (lang) => {
    // Update moment locale when language changes
    const momentLocale = this.getMomentLocale();
    moment.locale(momentLocale);

    // Force re-render to update all date formats
    this.forceUpdate();
  };

  componentDidUpdate(prevProps, prevState) {
    // Предотвращаем ненужные ре-рендеры
    if (
      prevState.postTasks !== this.state.postTasks ||
      prevState.selectedGroupId !== this.state.selectedGroupId
    ) {
      this.debouncedPreparePanelData();
    }

    // Call parent componentDidUpdate if it exists
    if (super.componentDidUpdate) {
      super.componentDidUpdate(prevProps, prevState);
    }
  }

  getWeekdays() {
    return [
      i18n.t("schedulePage.weekdays.mon"),
      i18n.t("schedulePage.weekdays.tue"),
      i18n.t("schedulePage.weekdays.wed"),
      i18n.t("schedulePage.weekdays.thu"),
      i18n.t("schedulePage.weekdays.fri"),
      i18n.t("schedulePage.weekdays.sat"),
      i18n.t("schedulePage.weekdays.sun"),
    ];
  }

  getDateFnsLocale() {
    const currentLanguage = i18n.language || "en";
    const localeMap = {
      en: enUS,
      ru: ru,
      fr: fr,
      de: de,
      it: it,
    };
    return localeMap[currentLanguage] || enUS;
  }

  getMomentLocale() {
    const currentLanguage = i18n.language || "en";
    const localeMap = {
      en: "en",
      ru: "ru",
      fr: "fr",
      de: "de",
      it: "it",
    };
    return localeMap[currentLanguage] || "en";
  }

  async loadData() {
    const { companyType } = this.props;

    await _request.postTasks.loading(async () => {
      try {
        const resp = await _request.postTasks.getAll(
          [],
          [],
          companyType || "invited"
        );
        this.setState({
          postTasks: resp?.data?.items || [],
          preparedPostsCache: null, // Очищаем кэш при загрузке новых данных
          lastPostTasksLength: 0,
        });

        const respUser = await _request.users.getAll();
        this.state.users = respUser?.data?.items || [];

        const respAcc = await _request.accounts.getAll();
        this.state.accounts = respAcc?.data?.items || [];

        // Without the groups the calendar is simply unfiltered, so a failure here must not break it.
        try {
          const respGroups = await _request.groups.getAll();
          this.setState({ groups: respGroups?.data?.items || [], preparedPostsCache: null, lastPostTasksLength: 0 });
        } catch (error) {
          console.error("Error loading account groups:", error);
          this.setState({ groups: [] });
        }

        // Предварительно подготавливаем данные после загрузки
        setTimeout(() => this.debouncedPreparePanelData(), 100);
      } catch (error) {
        console.error("Error loading data:", error);
      }
    });
  }
  toggleDrawer() {
    this.setState((prevState) => {
      const newOpenState = !prevState.open;

      // Если открываем панель, предварительно подготавливаем данные
      if (newOpenState) {
        this.debouncedPreparePanelData();
      }

      return {
        open: newOpenState,
      };
    });
  }
  handleClickMenu = (event, index) => {
    this.setState({
      anchorEl: event.currentTarget,
      openMenuIndex: index,
    });
  };

  handleClose = () => {
    this.setState({
      anchorEl: null,
      openMenuIndex: null,
    });
  };

  openPublishStatus = (parentPostId) => {
    this.handleClose();
    if (parentPostId) this.setState({ statusPostId: parentPostId });
  };

  closePublishStatus = () => {
    this.setState({ statusPostId: null, preparedPostsCache: null, lastPostTasksLength: 0 });
    this.refresh();
  };

  handleCancelSchedule = (parentPostId) => {
    _request.postTasks.loading(async () => {
      try {
        const tasksToDelete = this.state.postTasks.filter(
          (p) => p.parent_post_id === parentPostId
        );
        const deletePromises = tasksToDelete.map((task) =>
          _request.postTasks.delete(task.id)
        );
        await Promise.all(deletePromises);

        // Очищаем кэш при изменении данных
        this.setState({
          preparedPostsCache: null,
          lastPostTasksLength: 0,
        });

        this.refresh();
      } catch (error) {
        console.error("Error deleting tasks:", error);
      }
    });

    this.setState({
      anchorEl: null,
      openMenuIndex: null,
    });
  };

  // The composer only loads a post when it gets `editDraft` and a post-shaped object, and it only knows how to
  // replace a draft. A scheduled post is replaced the same way: the new post is created first, then these tasks go.
  handleEditPost = async (parentPostId) => {
    this.handleClose();
    if (!parentPostId) return;

    const tasks = this.state.postTasks.filter(
      (p) => p.parent_post_id === parentPostId
    );
    if (tasks.length === 0) return;

    let parentPost = null;
    try {
      const resp = await _request.posts.getAll(
        [],
        [{ field: "id", operator: "=", value: parentPostId }]
      );
      parentPost = resp?.data?.items?.[0] || null;
    } catch (error) {
      console.error("Error loading the post to edit:", error);
    }

    const { draftData, replaceTaskIds } = buildScheduledEdit(tasks, parentPost);
    this.props.navigate("/app/post", {
      state: {
        editDraft: true,
        draftData,
        scheduleEnabled: true,
        replaceTaskIds,
      },
    });
  };

  // The tasks the calendar shows: all of them, or those of the selected account group.
  getVisibleTasks() {
    const { postTasks, groups, selectedGroupId } = this.state;
    return filterTasksByGroup(postTasks, groups, selectedGroupId);
  }

  handleGroupChange = (event) => {
    // The side panel is built from a cache keyed by the number of tasks; a new filter must rebuild it.
    this.setState({
      selectedGroupId: event.target.value,
      preparedPostsCache: null,
      lastPostTasksLength: 0,
    });
  };

  getSocialTypesByParentId = (parentId) => {
    return this.getVisibleTasks().filter(
      (item) => item.parent_post_id === parentId
    );
  };

  // Предварительно подготавливаем данные для всплывающего окна
  preparePanelData = () => {
    if (!this.state.postTasks || this.state.postTasks.length === 0) {
      return;
    }

    // Проверяем, нужно ли обновлять кэш
    if (
      this.state.preparedPostsCache &&
      this.state.lastPostTasksLength === this.state.postTasks.length
    ) {
      return;
    }

    const uniqueTasks = this.filteredPostTask();
    const momentLocale = this.getMomentLocale();

    // Устанавливаем локаль один раз
    moment.locale(momentLocale);

    // Создаем кэш для socialTypes чтобы избежать повторных вычислений
    const socialTypesCache = {};

    const preparedData = uniqueTasks.map((post, index) => {
      const timeZone = post.timezone ? post.timezone : "UTC";
      const postDate = moment.utc(post.schedule_utc_date).tz(timeZone);
      const formattedDatePost = postDate.format("DD MMM HH:mm");
      const isPast = moment().isAfter(postDate);

      // Кэшируем socialTypes для избежания повторных вычислений
      let socialCount;
      if (post.parent_post_id) {
        if (!socialTypesCache[post.parent_post_id]) {
          socialTypesCache[post.parent_post_id] = this.getSocialTypesByParentId(
            post.parent_post_id
          );
        }
        socialCount = socialTypesCache[post.parent_post_id];
      } else {
        socialCount = [post.social_type];
      }

      const visibleIcons = socialCount.slice(0, 2);
      const additionalCount = socialCount.length - visibleIcons.length;
      const info = getPostInfo(post);
      const status = getPostStatus(
        post.parent_post_id ? socialCount : [post]
      );

      return {
        ...post,
        info,
        status,
        index,
        timeZone,
        postDate,
        formattedDatePost,
        isPast,
        socialCount,
        visibleIcons,
        additionalCount,
      };
    });

    // Кэшируем результат асинхронно
    setTimeout(() => {
      this.setState({
        preparedPostsCache: preparedData,
        lastPostTasksLength: this.state.postTasks.length,
      });
    }, 0);
  };

  // Получаем подготовленные данные (без setState внутри)
  getPreparedPostsData = () => {
    return this.state.preparedPostsCache || [];
  };

  filteredPostTask = () => {
    const postTasks = this.getVisibleTasks();

    const filteredTasks = postTasks.filter(
      (task, index, self) =>
        index ===
        self.findIndex((t) => t.parent_post_id === task.parent_post_id)
    );

    return filteredTasks.sort(
      (a, b) => new Date(b.schedule_utc_date) - new Date(a.schedule_utc_date)
    );
  };

  // One panel for the whole calendar: rendering it inside every day cell stacked 42 copies of
  // the cards and of the open Menu, which showed up as a huge blurred shadow around it.
  renderPostsPanel() {
    const { anchorEl, openMenuIndex } = this.state;

    return (
        <Box
          className={`custom-panel scrolling ${
            this.state.open ? "open" : "closed"
          }`}>
          <Box
            className="flex justify-start cursor-pointer"
            onClick={() => this.toggleDrawer()}>
            <CloseIcon sx={{ color: "#3E4955" }} />
          </Box>
          <Box className="flex column w-full gap-2">
            {this.getPreparedPostsData()
              .slice(0, 20)
              .map((post) => {
                const {
                  index,
                  formattedDatePost,
                  isPast,
                  visibleIcons,
                  additionalCount,
                  info,
                  status,
                } = post;

                return (
                  <Box key={index} className="flex flex-col gap-4 p-2">
                    <Box
                      className="p-3"
                      sx={{
                        backgroundColor: "var(--background-menu-list)",
                        borderRadius: "5px",
                      }}>
                      <Box className="flex relative">
                        <Box
                          sx={{
                            position: "relative",
                            width: 76,
                            height: 76,
                            flexShrink: 0,
                            marginRight: "12px",
                            borderRadius: "6px",
                            overflow: "hidden",
                            backgroundColor: "rgba(255,255,255,0.06)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}>
                          {info.files.length > 0 ? (
                            <ImageError
                              src={_request.files.formLink(info.files[0])}
                              className="image"
                              width="76px"
                              height="76px"
                              style={{ borderRadius: "6px" }}
                              loading="lazy"
                            />
                          ) : (
                            <NotesIcon sx={{ color: "var(--accent-secondary)", fontSize: 28 }} />
                          )}
                          {info.kind === "reel" && (
                            <PlayArrowIcon
                              sx={{
                                position: "absolute",
                                color: "#fff",
                                fontSize: 28,
                                filter: "drop-shadow(0 0 3px rgba(0,0,0,0.6))",
                              }}
                            />
                          )}
                          {info.files.length > 1 && (
                            <Box
                              sx={{
                                position: "absolute",
                                right: 4,
                                bottom: 4,
                                padding: "0 6px",
                                borderRadius: "10px",
                                fontSize: 11,
                                color: "#fff",
                                backgroundColor: "rgba(0,0,0,0.65)",
                              }}>
                              +{info.files.length - 1}
                            </Box>
                          )}
                        </Box>
                        <Box
  sx={{
  flex: 1,
  minWidth: 0,
  paddingRight: "24px",
  textAlign: "left",
  }}>
                          <Typography
                            noWrap
                            sx={{
                              fontSize: 15,
                              fontWeight: "bold",
                              color: info.title ? "var(--text-color)" : "var(--current-month)",
                            }}>
                            {info.title || i18n.t("schedulePage.untitled")}
                          </Typography>
                          {info.text && (
                            <Typography
                              sx={{
                                marginTop: "2px",
                                fontSize: 13,
                                lineHeight: 1.4,
                                color: "var(--current-month)",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}>
                              {info.text}
                            </Typography>
                          )}
                          <Box
                            className="flex items-center gap-2"
                            sx={{ marginTop: "8px", flexWrap: "wrap" }}>
                            {visibleIcons.map((socialType, key) => (
                              <IconsAccounts
                                key={key}
                                socialType={socialType.social_type}
                                width="20px"
                                height="16px"
                              />
                            ))}
                            {additionalCount > 0 && (
                              <Typography sx={{ fontSize: 12, color: "var(--text-color)" }}>
                                +{additionalCount}
                              </Typography>
                            )}
                            {info.kind !== "post" && (
                              <Box sx={chipSx("var(--accent-secondary)")}>
                                {i18n.t(`schedulePage.kind.${info.kind}`)}
                              </Box>
                            )}
                            {info.threadParts > 1 && (
                              <Box sx={chipSx("var(--accent-secondary)")}>
                                {i18n.t("schedulePage.threadChip", { n: info.threadParts })}
                              </Box>
                            )}
                            <Typography sx={{ fontSize: 12, color: "var(--accent-secondary)" }}>
                              {formattedDatePost}
                            </Typography>
                            <Box sx={chipSx(STATUS_COLORS[status])}>
                              {i18n.t(`schedulePage.status.${status}`)}
                            </Box>
                          </Box>
                        </Box>
                        <Box className="absolute top-0 -right-2">
                          <MoreVertIcon
                            onClick={(e) =>
                              this.handleClickMenu(e, index)
                            }
                            className="cursor-pointer"
                            sx={{ color: "var(--text-color)" }}
                          />
                        </Box>
                        <Menu
                          anchorEl={anchorEl}
                          open={openMenuIndex === index}
                          onClose={this.handleClose}
                          PaperProps={{
                            elevation: 0,
                            sx: {
                              overflow: "visible",
                              filter:
                                "drop-shadow(0px 0px 0px rgba(0,0,0,0.32))",
                              backgroundColor:
                                "var(--background-image)",
                              color: "var(--text-color)",
                              border:
                                "1px solid var(--accent-secondary)",
                            },
                          }}>
                          {isPast ? (
                            [
                              info.threadParts > 1 && post.parent_post_id && (
                                <MenuItem
                                  key="status"
                                  onClick={() =>
                                    this.openPublishStatus(post.parent_post_id)
                                  }>
                                  {i18n.t("schedulePage.publicationStatus")}
                                </MenuItem>
                              ),
                              <MenuItem
                                key="delete"
                                onClick={() =>
                                  this.handleCancelSchedule(post.id)
                                }>
                                {i18n.t("schedulePage.delete")}
                              </MenuItem>,
                            ]
                          ) : (
                            [
                              <MenuItem
                                key="edit"
                                onClick={() =>
                                  this.handleEditPost(
                                    post.parent_post_id
                                  )
                                }>
                                {i18n.t("schedulePage.edit")}
                              </MenuItem>,
                              <MenuItem
                                key="cancel"
                                onClick={() =>
                                  this.handleCancelSchedule(
                                    post.parent_post_id
                                  )
                                }>
                                {i18n.t("schedulePage.cancelSchedule")}
                              </MenuItem>,
                            ]
                          )}
                        </Menu>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            {this.getPreparedPostsData().length > 20 && (
              <Box className="p-2 text-center">
                <Typography
                  sx={{ color: "var(--text-color)", fontSize: 14 }}>
                  {i18n.t("schedulePage.showingFirst")} 20{" "}
                  {i18n.t("schedulePage.of")}{" "}
                  {this.getPreparedPostsData().length}{" "}
                  {i18n.t("schedulePage.posts")}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
    );
  }

  renderDayCell(day, monthStart, tasks, locale) {
    const entries = getDayEntries(tasks, day);
    const visible = entries.slice(0, MAX_CHIPS_PER_DAY);
    const weekday = (day.getDay() + 6) % 7;
    const classes = ["sch-cell"];
    if (!isSameMonth(day, monthStart)) classes.push("out");
    if (weekday > 4) classes.push("weekend");
    if (isSameDay(day, this.state.currentDay)) classes.push("today");
    if (entries.length) classes.push("has-posts");

    return (
      <div
        key={day.toISOString()}
        data-testid="sch-cell"
        className={classes.join(" ")}
        onClick={entries.length ? () => this.toggleDrawer() : undefined}>
        <div className="sch-head">
          <span className="sch-day">{format(day, "d", { locale })}</span>
          {entries.length > MAX_CHIPS_PER_DAY && (
            <span className="sch-more" data-testid="sch-more">+{entries.length - MAX_CHIPS_PER_DAY}</span>
          )}
        </div>
        <div className="sch-chips">
          {visible.map((post) => {
            const info = getPostInfo(post);
            const title = info.title || i18n.t("schedulePage.untitled");
            return (
              <div key={post.parent_post_id ?? post.id} className="sch-chip" data-testid="sch-chip" title={title}>
                <IconsAccounts socialType={post.social_type} width="14px" height="14px" />
                {post.count > 1 && <span className="sch-chip-count" data-testid="sch-chip-count">+{post.count - 1}</span>}
                <span className="sch-chip-title">{title}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  insertScheduleCalendar() {
    const { currentMonth } = this.state;
    const postTasks = this.getVisibleTasks();
    const locale = this.getDateFnsLocale();

    const monthStart = startOfMonth(currentMonth);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDate = endOfWeek(endOfMonth(monthStart), { weekStartsOn: 1 });

    const days = [];
    for (let day = startDate; day <= endDate; day = addDays(day, 1)) {
      days.push(day);
    }
    const weeks = days.length / 7;

    return (
      <>
        <div className="sch-cal">
          <div className="sch-weekdays">
            {this.getWeekdays().map((name) => (
              <div key={name}>{name}</div>
            ))}
          </div>
          <div
            className="sch-grid"
            style={{ gridTemplateRows: `repeat(${weeks}, minmax(var(--sch-row-min), 1fr))` }}>
            {days.map((day) => this.renderDayCell(day, monthStart, postTasks, locale))}
          </div>
        </div>
        {this.renderPostsPanel()}
      </>
    );
  }

  nextMonth = () => {
    this.setState({
      currentMonth: addMonths(this.state.currentMonth, 1),
    });
  };
  prevMonth = () => {
    this.setState({
      currentMonth: subMonths(this.state.currentMonth, 1),
    });
  };
  // handleClick = (value) => {
  //     this.setState({ selected: value });
  // };

  handleOpenWizard = () => {
    this.setState({ wizardOpen: true });
  };

  handleCloseWizard = () => {
    this.setState({ wizardOpen: false });
  };

  handleWizardComplete = () => {
    this.setState({ wizardOpen: false });
    // Refresh schedule data
    this.loadData();
  };

  // Shown only for users who have account groups; without them there is nothing to choose from.
  renderGroupFilter() {
    const { groups, selectedGroupId } = this.state;
    if (!groups.length) return null;

    // A group that disappeared since it was picked is treated as "All groups" (see filterTasksByGroup).
    const value = groups.some((group) => String(group.id) === String(selectedGroupId))
      ? selectedGroupId
      : ALL_GROUPS;

    return (
      <Select
        size="small"
        value={value}
        onChange={this.handleGroupChange}
        inputProps={{ "aria-label": i18n.t("schedulePage.groupFilter") }}
        data-testid="schedule-group-filter"
        sx={{
          minWidth: 180,
          maxWidth: 260,
          color: "var(--text-color)",
          backgroundColor: "var(--background-menu-list)",
        }}>
        <MenuItem value={ALL_GROUPS}>{i18n.t("schedulePage.allGroups")}</MenuItem>
        {groups.map((group) => (
          <MenuItem key={group.id} value={group.id}>
            {group.title}
          </MenuItem>
        ))}
      </Select>
    );
  }

  renderTopPanel() {
    const locale = this.getDateFnsLocale();
    const monthTitle = format(this.state.currentMonth, "LLLL yyyy", { locale });

    return (
      <Box className="sch-top">
        <Typography className="page-title sch-title" sx={{ color: "var(--text-color)" }}>
          {i18n.t("schedulePage.schedule")}
        </Typography>
        <Box className="sch-month">
          <IconButton size="small" onClick={this.prevMonth} aria-label={i18n.t("schedulePage.previousMonth")}>
            <ChevronLeftIcon sx={{ color: "var(--accent-secondary)" }} />
          </IconButton>
          <Typography className="sch-month-name">
            {monthTitle.charAt(0).toUpperCase() + monthTitle.slice(1)}
          </Typography>
          <IconButton size="small" onClick={this.nextMonth} aria-label={i18n.t("schedulePage.nextMonth")}>
            <ChevronRightIcon sx={{ color: "var(--accent-secondary)" }} />
          </IconButton>
        </Box>
        <Box className="sch-tools">
          {this.renderGroupFilter()}
          <Button
            variant="contained"
            startIcon={<AutoAwesomeIcon />}
            onClick={this.handleOpenWizard}
            sx={{
              backgroundColor: "var(--accent-primary)",
              color: "#000",
              whiteSpace: "nowrap",
              "&:hover": { backgroundColor: "var(--accent-secondary)" },
            }}>
            {i18n.t("schedulePage.aiCalendar")}
          </Button>
        </Box>
      </Box>
    );
  }

  render() {
    // Ensure locale is always up to date
    const momentLocale = this.getMomentLocale();
    moment.locale(momentLocale);

    return (
      <Box className="sch-page">
        {this.renderTopPanel()}
        {this.insertScheduleCalendar()}

        {/* AI Calendar Wizard */}
        <ContentCalendarWizard
          open={this.state.wizardOpen}
          onClose={this.handleCloseWizard}
          onComplete={this.handleWizardComplete}
        />

        <PublishStatusDialog
          open={Boolean(this.state.statusPostId)}
          postIds={this.state.statusPostId ? [this.state.statusPostId] : []}
          accounts={this.state.accounts}
          onClose={this.closePublishStatus}
          tr={(key, options) => i18n.t(key, options)}
          onReconnect={() => this.props.navigate("/app/accounts")}
        />
      </Box>
    );
  }
}

export { Schedule };
